import React, { useState } from "react";
import { AppEnv, ShowEnvBanner } from "../../Utils/Utils";
import { useLogout } from "../../hooks/useLogout";
import "./EnvironmentNotice.css";

// Display text only, keyed by REACT_APP_ENV — NOT the gate for whether this
// renders at all (see ShowEnvBanner for that). An env value with no entry
// here (or REACT_APP_ENV unset) falls back to a generic label rather than
// hiding the popup, since the boolean flag already owns that decision
// independently.
// Accent is the portal's own brand red (same value as LoginAuth/ErrorAndMaintenance),
// not a per-env color — this is a "heads up, non-prod" flag, not a severity scale.
const PORTAL_RED = "#da291c";
const ENV_LABELS: Record<string, string> = {
  dev: "Development",
  uat: "UAT",
};

// Announces which environment this build is running against.
// Deliberately shows on EVERY mount (every login, every full page reload) —
// no sessionStorage/localStorage dismissal memory. Chat.tsx itself doesn't
// remount on in-app navigation (switching sessions is just state, not a
// route remount), so this still only fires on an actual login or reload,
// not on every click.
//
// Gated entirely on REACT_APP_SHOW_ENV_BANNER, sourced per-environment from
// its own GitHub secret (…_DEV/_UAT/_PROD) so it can be flipped without a
// code change. It's deliberately a dedicated flag rather than "is
// REACT_APP_ENV a recognized non-prod value" — that keeps REACT_APP_ENV
// free to carry 'prod' too (for other uses) without that ever being able to
// flip this banner on. Merging this component to main is always safe as
// long as the Production secret stays "false".
const EnvironmentNotice: React.FC = () => {
  const warningEnabled = ShowEnvBanner();
  const env = AppEnv();
  const label = (env && ENV_LABELS[env]) || "Non-Production";

  const [isOpen, setIsOpen] = useState(warningEnabled);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logout = useLogout();

  const handleContinue = () => setIsOpen(false);

  // Cancel = the user doesn't want to be in this environment — send them
  // through the exact same logout flow as the sidebar's Log out (Okta
  // signOut + token revocation), not just a local dismiss.
  const handleCancel = async () => {
    setIsLoggingOut(true);
    await logout();
  };

  if (!warningEnabled || !isOpen) return null;

  return (
    // No onClick here — deliberately not dismissible via backdrop click or
    // Escape (no keydown listener either). Cancel/Continue below are the
    // only exits, so the environment can't be missed by an errant click.
    <div className="env-notice-overlay" role="presentation">
      <div
        className="env-notice-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="env-notice-title"
        style={{ borderTopColor: PORTAL_RED }}
      >
        <span className="env-notice-badge" style={{ background: PORTAL_RED }}>
          {label}
        </span>
        <h2 id="env-notice-title" className="env-notice-title">
          You're on the {label} environment
        </h2>
        <p className="env-notice-body">
          This is not the production environment. Data, files, and generated content here may
          be test data and should not be relied on as production-accurate.
        </p>
        <div className="env-notice-actions">
          <button
            type="button"
            className="env-notice-button env-notice-button--secondary"
            onClick={handleCancel}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging out…" : "Cancel"}
          </button>
          <button
            type="button"
            className="env-notice-button env-notice-button--primary"
            onClick={handleContinue}
            disabled={isLoggingOut}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentNotice;
